import * as React from "react";
import { useDispatch } from "redux-react-hook";
import styled, { ThemeContext } from "styled-components";

import { Select } from "src/library/components/Forms";
import Grid from "src/library/components/Hedron";
import {
  FontSizes,
  ThemeType
} from "src/library/utils/theme";
import {
  ISettingsResponse,
  UpdateGlobalSettingParams,
  useGlobalSettingsApi
} from "src/core/shared/api/globalSettings.api";
import { setAppSettings } from "src/core/shared/store/global/global.actions";

import Section from "./Section";
import Setting from "./Setting";

const availableFonts = [
  "Calibri",
  "Futura LT",
  "Lato",
  "Mocha Mattari",
  "Bryant Pro",
  "Merryweather",
  "Montserrat",
  "Muli",
  "Neo Hans",
  "Noto Sans",
  "Nunito Sans",
  "Open Sans",
  "Oswald",
  "Pieplar",
  "Poppins",
  "PT Sans",
  "PT Serif",
  "Raleway",
  "Roboto",
  "Roboto Slab",
  "Source Sans Pro",
  "Ubuntu",
  "Brandon Grotesque",
  "Claredon",
  "Microbrew Soft Four",
  "HumbleCraftman",
  "Poppins"
];

const ThemeFont: React.ReactFCWithChildren = () => {
  const [ globalSettings, globalSettingsApi ] = useGlobalSettingsApi();
  const [ updateGlobalSettingsResponse, updateGlobalSettings ] = globalSettingsApi<ISettingsResponse<"theme", ThemeType>>("UPDATE_SETTING");

  // update theme font on select
  const updateThemeFont = (fontType: "heading" | "body") => (e: React.ChangeEvent<HTMLSelectElement>) => {
    // get font name
    const updatedFont = e.target.value;

    if (globalSettings && globalSettings.theme) {
      // clone theme object
      const updatedTheme: ThemeType = { ...globalSettings.theme };

      // if only one font family specified, split into heading + body
      if (typeof globalSettings.theme.fonts.family === "string") {
        updatedTheme.fonts.family = {
          heading: globalSettings.theme.fonts.family,
          body: globalSettings.theme.fonts.family
        };
      }

      // update desired font
      updatedTheme.fonts.family[ fontType ] = updatedFont;

      // push updated theme to db
      updateGlobalSettings<UpdateGlobalSettingParams<ThemeType>>({
        name: "theme",
        data: updatedTheme
      });
    }
  };

  const updateThemeFontScale = (fontType: "heading" | "body") => (e: React.ChangeEvent<HTMLSelectElement>) => {
    const standardTitleScale: Record<string, number> = {
      h1: 28,
      h2: 20,
      h3: 18,
      h4: 14,
      h5: 12,
      h6: 10
    };

    const standardBodyScale: Record<string, number> = {
      sub: 10,
      p: 13
    };

    // get selected scale factor
    const scaleFactor = e.target.value;

    if (globalSettings && globalSettings.theme) {
      // select appropriate scale
      const selectedScale = fontType === "heading" ? standardTitleScale : standardBodyScale;
      // clone theme object
      const updatedTheme: ThemeType = { ...globalSettings.theme };
      // clone existing scale
      const updatedScale: FontSizes = { ...updatedTheme.fonts.sizes };

      // for each font size
      Object.keys(selectedScale).forEach(key => {
        // scale
        const scaled = selectedScale[ key ] * parseFloat(scaleFactor);
        // round to 1 d.p.
        const rounded = Math.round(scaled * 10) / 10;

        // transform to string with px suffix
        updatedScale[ key as keyof FontSizes ] = `${rounded}px`;
      });
      // update scale factor setting
      updatedScale[ `${fontType}Scale` as "headingScale" | "bodyScale" ] = scaleFactor;
      // apply update
      updatedTheme.fonts.sizes = updatedScale;

      // push updated sizing to db
      updateGlobalSettings<UpdateGlobalSettingParams<ThemeType>>({
        name: "theme",
        data: updatedTheme
      });
    }
  };

  const dispatch = useDispatch();

  // on successful theme change in db, update redux store
  React.useEffect(() => {
    if (globalSettings && updateGlobalSettingsResponse.data) {
      const updatedTheme: ThemeType = updateGlobalSettingsResponse.data.setting;

      dispatch(
        setAppSettings({
          ...globalSettings,
          theme: updatedTheme
        })
      );
    }
  }, [ updateGlobalSettingsResponse.data ]);

  const theme = React.useContext(ThemeContext);

  return (
    <Section title="Font">
      <Setting description="Customise title text display settings">
        <SelectList direction="horizontal">
          <Select
            as="select"
            name="select-title-font"
            autoHideLabels
            label="Select title font..."
            handleChange={updateThemeFont("heading")}
            defaultValue={theme.fonts.family.heading || "Roboto"}
            options={availableFonts.sort().map(fontName => ({
              text: fontName,
              value: fontName
            }))}
          />

          <Select
            as="select"
            name="select-title-font-size"
            autoHideLabels
            label="Select title scale..."
            handleChange={updateThemeFontScale("heading")}
            defaultValue={theme.fonts.sizes.headingScale || "1"}
            options={[
              {
                text: "80%",
                value: "0.8"
              },
              {
                text: "100%",
                value: "1"
              },
              {
                text: "120%",
                value: "1.2"
              },
              {
                text: "140%",
                value: "1.4"
              }
            ]}
          />
        </SelectList>
      </Setting>

      <Setting description="Customise body text display settings">
        <SelectList direction="horizontal">
          <Select
            as="select"
            name="select-body-font"
            autoHideLabels
            label="Select body text font..."
            handleChange={updateThemeFont("body")}
            defaultValue={theme.fonts.family.body || "Roboto"}
            options={availableFonts.map(fontName => ({
              text: fontName,
              value: fontName
            }))}
          />

          <Select
            as="select"
            name="select-body-font-size"
            autoHideLabels
            label="Select body text scale..."
            handleChange={updateThemeFontScale("body")}
            defaultValue={theme.fonts.sizes.bodyScale || "1"}
            options={[
              {
                text: "80%",
                value: "0.8"
              },
              {
                text: "100%",
                value: "1"
              },
              {
                text: "120%",
                value: "1.2"
              },
              {
                text: "140%",
                value: "1.4"
              }
            ]}
          />
        </SelectList>
      </Setting>
    </Section>
  );
};

const SelectList = styled(Grid.Bounds)`
  & > *:not(:first-child) {
    margin-left: 2rem;
  }
`;

export default ThemeFont;
